import { FormEvent, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  DeepPartial,
  FieldValues,
  useForm,
  UseFormReturn,
  ValidationMode,
} from 'react-hook-form'

import * as Yup from 'yup'
import useDeepEffect from './useDeepEffect'
import { DateTime } from 'luxon'
import stateOptions from 'src/utils/stateOptions'

// Default Yup error message for required fields
Yup.setLocale({
  mixed: {
    default: 'This field has an invalid value.',
    required: 'This field is required.',
  },
})

export type FormHandler<T extends FieldValues> = {
  form: UseFormReturn<T, any>
  error?: Error
  onSubmit: (e?: FormEvent) => Promise<void>
}

export function useFormHandler<T extends FieldValues = Record<string, unknown>>(
  handleSubmit: (values: T) => void,
  defaultValues: DeepPartial<T>,
  schema: Yup.AnyObjectSchema,
  dontClearErrorsOnSuccess = false,
  mode: keyof ValidationMode,
): FormHandler<T> {
  const form = useForm<T>({
    defaultValues,
    resolver: schema ? yupResolver(schema) : undefined,
    mode,
  })
  const [error, setError] = useState<Error | undefined>()

  const onSubmit = form.handleSubmit(async values => {
    try {
      await handleSubmit(values)

      // Bypassing the error clearing step is useful for whenever the user gets navigated away
      // from the screen in the submit handler. By not bothering to clear the errors, we avoid a console error
      // from popping up and warning about a memory leak.
      if (!dontClearErrorsOnSuccess) {
        form.clearErrors()
        // eslint-disable-next-line no-throw-literal
        setError(undefined)
      }
    } catch (e) {
      setError(e as Error)
    }
  })

  useDeepEffect(() => {
    // Reset the form anytime the default values change
    form.reset(defaultValues)
  }, [defaultValues])

  return {
    onSubmit,
    form,
    error,
  }
}

export const dateOfBirthValidation = Yup.string()
  .required()
  .when((dob: any, schema: any) => {
    return schema.test(
      'test DOB validity',
      'Please enter a valid date of birth.',
      (input: string) => {
        if (input.length < 10) {
          return false
        }

        const regexDateFormatMatch = input.match(
          /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        )

        if (!regexDateFormatMatch) {
          return false
        }

        try {
          const [month, day, year] = input.split('/')
          const fullYear = `${year.length === 2 ? '20' : ''}${year}`

          const formattedDate = DateTime.fromObject({
            year: Number(fullYear),
            month: Number(month),
            day: Number(day),
          })

          const dateTimeToday = DateTime.fromJSDate(new Date())

          const differenceInYears = dateTimeToday.diff(formattedDate, 'years')
            .years

          const isInvalidDate = isNaN(differenceInYears)

          if (isInvalidDate) {
            return false
          }

          const impossibleAgeMaxRange = differenceInYears >= 125
          const impossibleAgeMinRange = differenceInYears <= 0

          if (impossibleAgeMaxRange || impossibleAgeMinRange) {
            return false
          }
        } catch (error) {
          return false
        }

        return true
      },
    )
  })

export const zipCodeValidation = Yup.string()
  .required()
  .matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, 'Please enter a valid zip code.')

export const emailValidation = Yup.string()
  .email('Please enter a valid email address. (e.g. example@example.org)')
  .required()

const stateAbbreviations = stateOptions.map(option => option.value)
export const stateValidation = Yup.string().oneOf(stateAbbreviations)

export const phoneValiation = Yup.string()
  .required()
  .matches(
    /^\+\d{1,2}\s\(\d{3}\)\s\d{3}-\d{4}$/,
    'Please enter a valid phone number.',
  )
